import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";

import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const INTER = 'Inter, "Space Grotesk", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

const useStyles = makeStyles(theme => ({
  page: { minHeight: "100vh", background: theme.app.chatBg, overflowY: "auto" },
  top: { display: "flex", justifyContent: "flex-end", padding: theme.spacing(2, 3) },
  skip: { background: "none", border: 0, color: theme.app.textTertiary, fontSize: 13, cursor: "pointer", fontFamily: theme.typography.fontFamily },
  wrap: { maxWidth: 960, margin: "0 auto", padding: theme.spacing(1, 3, 8) },
  title: { fontFamily: INTER, fontWeight: 900, fontSize: 44, lineHeight: 1.1, color: theme.app.ink, letterSpacing: -0.5, margin: theme.spacing(2, 0, 1) },
  sub: { color: theme.app.textSecondary, fontSize: 15, marginBottom: theme.spacing(3.5) },
  block: { marginBottom: theme.spacing(4) },
  qlabel: { fontSize: 13, fontWeight: 600, color: theme.app.textSecondary, marginBottom: theme.spacing(1.25), textTransform: "none" },
  chips: { display: "flex", flexWrap: "wrap", gap: 10 },
  chip: {
    display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 16px", borderRadius: 999,
    border: `1px solid ${theme.app.border}`, background: "#FFF", color: theme.app.ink, fontSize: 14, fontWeight: 500,
    cursor: "pointer", fontFamily: theme.typography.fontFamily, transition: "all .12s",
    "&:hover": { background: theme.app.selectedRow },
  },
  chipOn: { borderColor: theme.app.ink, background: theme.app.ink, color: "#FFF", "&:hover": { background: theme.app.ink } },
  chipDashed: { borderStyle: "dashed", color: theme.app.textSecondary },
  mono: { width: 24, height: 24, borderRadius: "50%", background: "#FFF", border: `1px solid ${theme.app.border}`, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: theme.app.textSecondary },
  grid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" } },
  card: { background: "#FFF", border: `1px solid ${theme.app.border}`, borderRadius: 14, padding: theme.spacing(2.25), display: "flex", flexDirection: "column", minHeight: 240 },
  cardFirst: { borderColor: theme.app.ink, borderWidth: 1.5 },
  seal: { alignSelf: "flex-start", fontSize: 11, fontWeight: 700, color: theme.app.ink, background: theme.app.lime, borderRadius: 999, padding: "3px 10px", marginBottom: 10 },
  ctitle: { fontFamily: theme.app.heading, fontSize: 17, fontWeight: 600, color: theme.app.ink, marginBottom: 6 },
  cscene: { fontSize: 13.5, color: theme.app.textSecondary, lineHeight: 1.45, marginBottom: 14 },
  steps: { display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 },
  step: { display: "flex", gap: 10, alignItems: "baseline" },
  stepVia: { fontSize: 11.5, fontWeight: 700, color: theme.app.textSecondary, minWidth: 92, textTransform: "none" },
  stepAct: { fontSize: 13, color: theme.app.ink },
  precisa: { fontSize: 12.5, color: theme.app.textTertiary, marginTop: "auto", marginBottom: 12 },
  cta: { textTransform: "none", fontWeight: 600, borderRadius: 10, boxShadow: "none" },
  ctaGreen: { background: theme.app.lime, color: theme.app.ink, "&:hover": { background: theme.app.limeHover, boxShadow: "none" } },
  ctaOutline: { border: `1px solid ${theme.app.border}`, color: theme.app.ink, background: "#FFF" },
  note: { background: "#FFF", border: `1px dashed ${theme.app.border}`, borderRadius: 12, padding: theme.spacing(2.5), color: theme.app.textSecondary, fontSize: 14 },
  outroRow: { display: "flex", gap: 10, marginTop: 12, maxWidth: 420 },
}));

const sub = (s, sistema) => String(s || "").replace(/\{sistema\}/g, sistema || "seu sistema");

const Recepcao = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, setUser } = useContext(AuthContext);

  const [areas, setAreas] = useState([]);
  const [areaKey, setAreaKey] = useState(null);
  const [systemKey, setSystemKey] = useState(null); // chave de conector, ou "__outro__" / "__nenhum__"
  const [outroText, setOutroText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/onboarding/catalog");
        setAreas(data.areas || []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  const area = useMemo(() => areas.find(a => a.key === areaKey) || null, [areas, areaKey]);
  const systemLabel = useMemo(() => {
    const s = area && area.systems.find(x => x.key === systemKey);
    return s ? s.label : "";
  }, [area, systemKey]);

  const pickArea = a => {
    setAreaKey(a.key);
    setSystemKey(a.systems[0] ? a.systems[0].key : null);
    setOutroText("");
  };

  const markOnboarded = () => setUser && user && setUser({ ...user, onboarded: true });

  const skip = async () => {
    try {
      await api.post("/me/onboarding", { skip: true });
    } catch (e) { /* segue mesmo assim */ }
    markOnboarded();
    history.push("/");
  };

  const sendSignal = async kind => {
    try {
      await api.post("/me/onboarding", { area: areaKey, signal: { kind, area: areaKey, text: kind === "outro" ? outroText : undefined } });
    } catch (e) { /* silencioso */ }
  };

  const reguasFor = area ? area.reguas.filter(r => r.systems.includes(systemKey)) : [];

  const montar = async regua => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/me/onboarding/deploy", { regua: regua.key, system: systemKey, area: areaKey });
      markOnboarded();
      history.push(`/applications/${data.appId}`);
    } catch (err) {
      toastError(err);
      setBusy(false);
    }
  };

  const isOutro = systemKey === "__outro__";
  const isNenhum = systemKey === "__nenhum__";

  return (
    <div className={classes.page}>
      <div className={classes.top}>
        <button className={classes.skip} onClick={skip}>Pular — quero explorar sozinho</button>
      </div>
      <div className={classes.wrap}>
        <h1 className={classes.title}>O que você resolveria hoje, se não precisasse pedir pra TI?</h1>
        <div className={classes.sub}>Escolha sua área e a ferramenta que você usa. A Numera mostra a régua pronta, com o nome do seu sistema dentro dos passos.</div>

        {/* 1. áreas */}
        <div className={classes.block}>
          <div className={classes.qlabel}>O que você faz?</div>
          <div className={classes.chips} role="radiogroup" aria-label="Área">
            {areas.map(a => (
              <button
                key={a.key}
                role="radio"
                aria-checked={areaKey === a.key}
                className={`${classes.chip} ${areaKey === a.key ? classes.chipOn : ""}`}
                onClick={() => pickArea(a)}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>

        {/* 2. sistemas */}
        {area && (
          <div className={classes.block}>
            <div className={classes.qlabel}>{area.question}</div>
            <div className={classes.chips} role="radiogroup" aria-label="Sistema">
              {area.systems.map(s => (
                <button
                  key={s.key}
                  role="radio"
                  aria-checked={systemKey === s.key}
                  className={`${classes.chip} ${systemKey === s.key ? classes.chipOn : ""}`}
                  onClick={() => setSystemKey(s.key)}
                >
                  <span className={classes.mono}>{s.label.slice(0, 1).toUpperCase()}</span>
                  {s.label}
                </button>
              ))}
              <button className={`${classes.chip} ${classes.chipDashed} ${isOutro ? classes.chipOn : ""}`} onClick={() => setSystemKey("__outro__")}>Outro</button>
              <button className={`${classes.chip} ${classes.chipDashed} ${isNenhum ? classes.chipOn : ""}`} onClick={() => { setSystemKey("__nenhum__"); sendSignal("nenhum"); }}>Não uso nenhum</button>
            </div>
          </div>
        )}

        {/* 3. réguas / sinais */}
        {area && isOutro && (
          <div className={classes.block}>
            <div className={classes.note}>
              Qual sistema você usa? A gente prioriza os próximos conectores pelo que vocês pedem.
              <div className={classes.outroRow}>
                <TextField size="small" variant="outlined" fullWidth placeholder="Nome do sistema" value={outroText} onChange={e => setOutroText(e.target.value)} />
                <Button variant="contained" className={`${classes.cta} ${classes.ctaGreen}`} disabled={!outroText.trim()} onClick={async () => { await sendSignal("outro"); setOutroText(""); }}>Enviar</Button>
              </div>
            </div>
          </div>
        )}

        {area && isNenhum && (
          <div className={classes.block}>
            <div className={classes.note}>Anotado. As réguas sobre planilha (Tabelas da Numera) entram em breve — por enquanto, escolha um sistema acima ou explore sozinho.</div>
          </div>
        )}

        {area && !isOutro && !isNenhum && (
          <div className={classes.block}>
            {reguasFor.length === 0 ? (
              <div className={classes.note}>Ainda não temos uma régua pronta para essa combinação. Escolha outro sistema, ou toque em “Pular” para montar do seu jeito.</div>
            ) : (
              <div className={classes.grid}>
                {reguasFor.map((r, i) => (
                  <div key={r.key} className={`${classes.card} ${i === 0 ? classes.cardFirst : ""}`}>
                    {i === 0 && <span className={classes.seal}>Comece por esta</span>}
                    <div className={classes.ctitle}>{sub(r.title, systemLabel)}</div>
                    <div className={classes.cscene}>{sub(r.scene, systemLabel)}</div>
                    <div className={classes.steps}>
                      {r.steps.map((st, j) => (
                        <div key={j} className={classes.step}>
                          <span className={classes.stepVia}>{sub(st.via, systemLabel)}</span>
                          <span className={classes.stepAct}>{sub(st.action, systemLabel)}</span>
                        </div>
                      ))}
                    </div>
                    <div className={classes.precisa}>Precisa: {sub(r.precisa, systemLabel)}</div>
                    <Button
                      variant="contained"
                      disabled={busy}
                      className={`${classes.cta} ${i === 0 ? classes.ctaGreen : classes.ctaOutline}`}
                      onClick={() => montar(r)}
                    >
                      Montar com {systemLabel}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Recepcao;
